import { browserStorage } from "@utils/browserStorage";
import { fetchAuthSession } from "aws-amplify/auth";

const REGION = "ap-southeast-2";

interface EnvConfig {
  /** Host that selects this env, e.g. "delta.catchment.software". "" = localhost/dev fallback. */
  host: string;
  /**
   * Hosts that still resolve to this env after a domain move. Matching stays
   * additive so a user on the old address keeps the same pools and gateway
   * instead of falling through to the dev fallback — see ADR-0001 Phase 4.
   */
  legacyHosts?: string[];
  saml: boolean;
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId: string;
  apiEndpoint: string;
  /** Present → Amplify oauth block. Absent → USER_SRP_AUTH (prod / dev). */
  cognitoDomain?: string;
  /** Per-env overrides; only mike/kamilia diverge from the prod/dev resource tier. */
  bucketName?: string;
  llmApiUrl?: string;
}

// Prod-tier resources. Used by prod AND every SAML customer host (see IS_PROD_ENV below).
const PROD_RESOURCES = {
  bucketName: "storagestack-prod-databucketd8691f4e-158dh2vnoby26",
  cloudFrontDomain: "https://d3gwzayzgl35rn.cloudfront.net",
  // ponytail: prod LLM URL intentionally identical to dev (confirmed)
  llmApiUrl:
    "https://q2o4ajncjcnbqgaycgfu5c3mqy0kapus.lambda-url.ap-southeast-2.on.aws/",
  mcpApiUrl: "https://mcp.catchment.software",
  publicApiUrl: "https://api.catchment.software"
};

const DEV_DEFAULTS = {
  bucketName: "storagestack-dev-databucketd8691f4e-kkco8jw9wvo9",
  cloudFrontDomain: "https://d1tej3s1mcawlu.cloudfront.net",
  llmApiUrl:
    "https://q2o4ajncjcnbqgaycgfu5c3mqy0kapus.lambda-url.ap-southeast-2.on.aws/",
  mcpApiUrl: "https://dev-mcp.catchment.software",
  publicApiUrl: "https://uwb8n0w2ih.execute-api.ap-southeast-2.amazonaws.com/v1"
};

// Amplify REST gateway. SHARED is the 8mfqbpfm8h gateway used by prod + all SAML hosts,
// reached via the backend.catchment.software named route so corporate firewalls that
// allowlist a specific vendor domain (but block raw *.execute-api.amazonaws.com) can hit it.
// ponytail: assumes the API-GW custom domain uses an EMPTY base-path mapping to the `dev`
// stage → no /dev/ in the path. If it was mapped with base path "dev", make this
// "https://backend.catchment.software/dev/". Verify with the curl in the PR notes before deploy.
// FALLBACK (h1zp83gkok) is used only by the dev/localhost fallback env — left on the raw
// endpoint; local dev isn't behind the customer firewall.
const SHARED_API_GATEWAY = "https://backend.catchment.software/";
const FALLBACK_API_GATEWAY =
  "https://h1zp83gkok.execute-api.ap-southeast-2.amazonaws.com/dev/";

// CloudFront-fronted named route for cognito-idp.ap-southeast-2.amazonaws.com, so prod
// SRP logins survive firewalls that block raw *.amazonaws.com. Prod only (see below);
// dev/localhost keep calling Cognito directly.
const PROD_AUTH_ENDPOINT = "https://auth.catchment.software";
// Sibling proxy for cognito-identity.ap-southeast-2.amazonaws.com (Identity Pool —
// hands out AWS creds for S3 access). Separate origin host, so its own subdomain.
const PROD_IDENTITY_POOL_ENDPOINT = "https://identity.catchment.software";

type EnvKey =
  | "prod"
  | "daniel"
  | "mike"
  | "demo"
  | "kamilia"
  | "delta"
  | "multiplex"
  | "symal"
  | "kapitol"
  | "sec"
  | "goodman"
  | "bmd"
  | "dev";

const ENVS: Record<EnvKey, EnvConfig> = {
  bmd: {
    apiEndpoint: SHARED_API_GATEWAY,
    cognitoDomain: "social-pro-auth.auth.ap-southeast-2.amazoncognito.com",
    host: "bmd.catchment.software",
    identityPoolId: "ap-southeast-2:5a6ce371-b239-4187-9aa9-00e89e1b3a50",
    saml: true,
    userPoolClientId: "4ahtutnsnjpht36ipgm1srhmbf",
    userPoolId: "ap-southeast-2_ax9BilHXQ"
  },
  // Shares the prod user pool/SAML client with kapitol/sec/goodman — see
  // that comment below. New on catchment.software rather than
  // socialproapp.com: bmd was decommissioned (own dedicated stack,
  // never deleted but no longer deployed) before this domain existed,
  // so there's no live legacy host to stay additive with.
  daniel: {
    apiEndpoint:
      "https://8y6gqnjjxj.execute-api.ap-southeast-2.amazonaws.com/dev/",
    bucketName: "storagestack-dev-databucketd8691f4e-xwexxtn1x7b2",
    host: "daniel.socialproapp.com",
    identityPoolId: "ap-southeast-2:f47ddf97-5401-4aba-a21e-d0c532e3aed7",
    llmApiUrl:
      "https://uiafexs6ctj4l6f4mjrq6xed3i0gslbh.lambda-url.ap-southeast-2.on.aws/",
    saml: false,
    userPoolClientId: "4e1a87332gnuclbamofg8ir53s",
    userPoolId: "ap-southeast-2_tnaIgiG70"
  },
  delta: {
    apiEndpoint: SHARED_API_GATEWAY,
    cognitoDomain:
      "delta-social-pro-saml-signin.auth.ap-southeast-2.amazoncognito.com",
    host: "delta.catchment.software",
    identityPoolId: "ap-southeast-2:38c44d43-21ff-4e0d-be87-224d19f95a31",
    saml: true,
    userPoolClientId: "4o33rnrshf636psdab0d7tk0e6",
    userPoolId: "ap-southeast-2_1J119snJK"
  },
  demo: {
    apiEndpoint: FALLBACK_API_GATEWAY,
    host: "",
    identityPoolId: "ap-southeast-2:c9b4e82b-47e9-42a8-87bf-a49bf4d2c9d7",
    saml: false,
    userPoolClientId: "358am0h5s4he0392f6g0aedd5",
    userPoolId: "ap-southeast-2_wFuraZnLs"
  },
  // Fallback: localhost + dev.catchment.software (host: "" never matches, so it's the default).
  dev: {
    apiEndpoint: FALLBACK_API_GATEWAY,
    host: "",
    identityPoolId: "ap-southeast-2:c9b4e82b-47e9-42a8-87bf-a49bf4d2c9d7",
    saml: false,
    userPoolClientId: "358am0h5s4he0392f6g0aedd5",
    userPoolId: "ap-southeast-2_wFuraZnLs"
  },
  goodman: {
    apiEndpoint: SHARED_API_GATEWAY,
    cognitoDomain: "social-pro-auth.auth.ap-southeast-2.amazoncognito.com",
    host: "goodman.catchment.software",
    identityPoolId: "ap-southeast-2:5a6ce371-b239-4187-9aa9-00e89e1b3a50",
    saml: true,
    userPoolClientId: "4ahtutnsnjpht36ipgm1srhmbf",
    userPoolId: "ap-southeast-2_ax9BilHXQ"
  },
  kamilia: {
    apiEndpoint:
      "https://p1w4mzjp7a.execute-api.ap-southeast-2.amazonaws.com/dev/",
    bucketName: "storagestack-dev-databucketd8691f4e-ysw7hhcghs0z",
    cognitoDomain:
      "social-pro-sandbox-social-pro-auth.auth.ap-southeast-2.amazoncognito.com",
    host: "kamilia.socialproapp.com",
    identityPoolId: "ap-southeast-2:7b8853ec-8742-4832-aed5-531abde66b32",
    saml: false,
    userPoolClientId: "1p5eogp57npvieqb3dq848aih4",
    userPoolId: "ap-southeast-2_oSOo8FTJK"
  },
  // kapitol/sec/goodman/bmd: identical config (prod user pool + SAML oauth), distinct hosts only.
  kapitol: {
    apiEndpoint: SHARED_API_GATEWAY,
    cognitoDomain: "social-pro-auth.auth.ap-southeast-2.amazoncognito.com",
    host: "kapitol.catchment.software",
    identityPoolId: "ap-southeast-2:5a6ce371-b239-4187-9aa9-00e89e1b3a50",
    saml: true,
    userPoolClientId: "4ahtutnsnjpht36ipgm1srhmbf",
    userPoolId: "ap-southeast-2_ax9BilHXQ"
  },
  mike: {
    apiEndpoint:
      "https://apb6mnz8yb.execute-api.ap-southeast-2.amazonaws.com/dev/",
    bucketName: "storagestack-dev-databucketd8691f4e-z2ovsbhtdcvs",
    cognitoDomain:
      "sandbox-social-pro-auth.auth.ap-southeast-2.amazoncognito.com",
    host: "mike.socialproapp.com",
    identityPoolId: "ap-southeast-2:8a5a7f4b-c11e-409f-8416-8b493560b701",
    llmApiUrl:
      "https://aoyspflhnbllv6dzepbdhn76ra0twigq.lambda-url.ap-southeast-2.on.aws/",
    saml: false,
    userPoolClientId: "2j322i4m6lau9nggv7lfr1a4ns",
    userPoolId: "ap-southeast-2_jcTiBmApM"
  },
  multiplex: {
    apiEndpoint: SHARED_API_GATEWAY,
    cognitoDomain:
      "multiplex-social-pro-saml-signin.auth.ap-southeast-2.amazoncognito.com",
    host: "multiplex.catchment.software",
    identityPoolId: "ap-southeast-2:9d6cf42f-5ec9-4663-be06-c93ee5fad51c",
    saml: true,
    userPoolClientId: "2qf4i0q4o5gqu92h159nb55t1e",
    userPoolId: "ap-southeast-2_6HOmMrJDz"
  },
  // Resource URLs/bucket come from PROD_RESOURCES (shared with SAML hosts via IS_PROD_ENV).
  prod: {
    apiEndpoint: SHARED_API_GATEWAY,
    host: "au.catchment.software",
    identityPoolId: "ap-southeast-2:5a6ce371-b239-4187-9aa9-00e89e1b3a50",
    legacyHosts: ["portal.socialproapp.com"],
    saml: false,
    userPoolClientId: "26l7l8n3747i1hffg34e1isctf",
    userPoolId: "ap-southeast-2_ax9BilHXQ"
  },
  sec: {
    apiEndpoint: SHARED_API_GATEWAY,
    cognitoDomain: "social-pro-auth.auth.ap-southeast-2.amazoncognito.com",
    host: "sec.catchment.software",
    identityPoolId: "ap-southeast-2:5a6ce371-b239-4187-9aa9-00e89e1b3a50",
    saml: true,
    userPoolClientId: "4ahtutnsnjpht36ipgm1srhmbf",
    userPoolId: "ap-southeast-2_ax9BilHXQ"
  },
  symal: {
    apiEndpoint: SHARED_API_GATEWAY,
    cognitoDomain:
      "symal-social-pro-saml-signin.auth.ap-southeast-2.amazoncognito.com",
    host: "symal.catchment.software",
    identityPoolId: "ap-southeast-2:d0d0b1d9-76c3-46ba-ad4e-4acb7afa8efb",
    saml: true,
    userPoolClientId: "6fcav1q120vo1fhseiivi69tf1",
    userPoolId: "ap-southeast-2_VvWhBPKpr"
  }
};

const normalizeHost = (host: string) =>
  host
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .toLowerCase();

const resolveEnvKey = (): EnvKey => {
  // VITE_SANDBOX_HOST is inlined by the `define` block in vite.config.mjs;
  // under Jest it's just an unset env var, so tests fall through to window.location.
  const currentHost = normalizeHost(
    process.env.VITE_SANDBOX_HOST || window.location.host
  );
  const match = (Object.keys(ENVS) as EnvKey[]).find(
    (key) =>
      ENVS[key].host === currentHost ||
      ENVS[key].legacyHosts?.includes(currentHost)
  );
  return match ?? "dev";
};

/**
 * LocalStack overrides, inlined by vite's `define` from frontend/.localstack.json
 * (written by `pnpm run localstack:up` in backend/). Empty for every normal build,
 * so this whole branch drops out unless you ran `pnpm run dev:localstack`.
 */
const LOCALSTACK:
  | (EnvConfig & { endpoint: string; s3Endpoint: string })
  | undefined = process.env.VITE_LOCALSTACK_CONFIG
  ? JSON.parse(process.env.VITE_LOCALSTACK_CONFIG)
  : undefined;

/**
 * Scheme-less S3 host for LocalStack, or undefined everywhere else.
 *
 * Amplify has no global config knob for a custom S3 endpoint that works here:
 * `Storage.S3.dangerouslyConnectToHttpEndpointForTesting` is a flag that pins the
 * endpoint to Amplify Mock's `http://localhost:20005`, and the resolver then rejects
 * it anyway for containing "://" (aws-amplify/amplify-js#13961). The only usable hook
 * is the per-call `options.customEndpoint`, which `utils/storage/shared.ts` spreads
 * into every storage call. It must be scheme-less — Amplify prefixes https itself.
 */
export const LOCALSTACK_S3_ENDPOINT = LOCALSTACK?.s3Endpoint;

// Resolve as "dev" so every predicate below (isProdEnv, isSaml, IS_PROD_ENV) keeps
// its dev-fallback behaviour; only the resource ids come from LocalStack.
export const ENV_KEY = LOCALSTACK ? "dev" : resolveEnvKey();
const ENV: EnvConfig = LOCALSTACK ?? ENVS[ENV_KEY];

// --- Host predicates (kept as exports: callers across the app rely on them) ---
export const isProdEnv = () => ENV_KEY === "prod";
// Port-agnostic: vite falls back to 3001+ when 3000 is taken, and the old
// hardcoded ":3000" check let those runs initialise Sentry against the real DSN.
export const isLocalHost = () => window.location.hostname === "localhost";
export const isBMD = () => ENV_KEY === "bmd";
export const isDelta = () => ENV_KEY === "delta";
export const isMultiplex = () => ENV_KEY === "multiplex";
export const isSymal = () => ENV_KEY === "symal";
export const isKapitol = () => ENV_KEY === "kapitol";
export const isSEC = () => ENV_KEY === "sec";
export const isGoodman = () => ENV_KEY === "goodman";
export const isSaml = () => ENV.saml;

/**
 * Match HTTPS requests to any *.catchment.software app host (au, customer shells, dev).
 * Excludes api.catchment.software (Public API) and auth./identity.catchment.software
 * (Cognito named routes) so Sentry does not attach sentry-trace/baggage headers to them —
 * Cognito's InitiateAuth preflight would reject the extra allow-headers and break login.
 * New customer subdomains under this domain do not need listing — only add patterns for
 * other domains.
 */
export const CATCHMENT_APP_TRACE_PROPAGATION_PATTERN =
  /^https:\/\/(?!api\.|auth\.|identity\.)([a-z0-9-]+\.)+catchment\.software(:\d+)?(\/|$)/;

// SAML customer hosts run on prod resources (old code: IS_PROD_ENV = isProdEnv() || isSaml()).
export const IS_PROD_ENV = isProdEnv() || isSaml();
export const IS_TEST_ENV =
  !IS_PROD_ENV && (window as any).PLAYWRIGHT_RUNNING === true;

// These five constants are prod-vs-dev tiered (old behaviour keyed them off IS_PROD_ENV,
// not the per-host config), with mike's per-env overrides layered on top.
const RESOURCES = IS_PROD_ENV ? PROD_RESOURCES : DEV_DEFAULTS;

export const PUBLIC_API_URL = RESOURCES.publicApiUrl;
export const MCP_API_URL = RESOURCES.mcpApiUrl;
/** Set via VITE_LLM_API_URL after deploying `LlmStack` (Lambda Function URL). */
export const LLM_API_URL = ENV.llmApiUrl ?? RESOURCES.llmApiUrl;
export const CLOUD_FRONT_DOMAIN_NAME = RESOURCES.cloudFrontDomain;
// Public (no-auth) employee invite endpoint — same API Gateway, separate resource with no authorizer.
export const EMPLOYEE_INVITE_PUBLIC_URL = `${ENV.apiEndpoint.replace(/\/$/, "")}/employee-invite-public`;

export const BUCKET_NAME = ENV.bucketName ?? RESOURCES.bucketName;

const Auth = {
  currentSession: async () => {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString() || "";
    return {
      getAccessToken: () => ({
        getJwtToken: () => accessToken
      })
    };
  }
};

export const normalizeAmplifyV6Config = (legacyConfig: any) => {
  const config = { ...legacyConfig };

  if (config.Auth && !config.Auth.Cognito) {
    const { oauth, userPoolWebClientId, ...restAuth } = config.Auth;
    config.Auth = {
      Cognito: {
        ...restAuth,
        ...(userPoolWebClientId
          ? {
              userPoolClientId: userPoolWebClientId
            }
          : {}),
        ...(oauth
          ? {
              loginWith: {
                oauth: {
                  domain: oauth.domain,
                  redirectSignIn: Array.isArray(oauth.redirectSignIn)
                    ? oauth.redirectSignIn
                    : [oauth.redirectSignIn],
                  redirectSignOut: Array.isArray(oauth.redirectSignOut)
                    ? oauth.redirectSignOut
                    : [oauth.redirectSignOut],
                  responseType: oauth.responseType,
                  scopes: oauth.scope || oauth.scopes || []
                }
              }
            }
          : {})
      }
    };
  }

  if (config.API?.endpoints && !config.API?.REST) {
    const rest: Record<
      string,
      { endpoint: string; headers?: () => Promise<Record<string, string>> }
    > = {};
    for (const endpointConfig of config.API.endpoints) {
      rest[endpointConfig.name] = {
        endpoint: endpointConfig.endpoint,
        ...(endpointConfig.custom_header
          ? {
              headers: endpointConfig.custom_header
            }
          : {})
      };
    }
    config.API = { REST: rest };
  }

  if (config.Storage?.AWSS3 && !config.Storage?.S3) {
    config.Storage = {
      S3: config.Storage.AWSS3
    };
  }

  return config;
};

const authHeader = async () => ({
  Authorization: `${(await Auth.currentSession())
    .getAccessToken()
    .getJwtToken()}`
});

export const getAmplifyConfiguration = () => ({
  API: {
    endpoints: [
      {
        custom_header: authHeader,
        endpoint: ENV.apiEndpoint,
        name: "api"
      }
    ]
  },
  Auth: {
    identityPoolId: ENV.identityPoolId,
    mandatorySignIn: true,
    region: REGION,
    storage: browserStorage,
    userPoolId: ENV.userPoolId,
    userPoolWebClientId: ENV.userPoolClientId,
    ...(ENV.cognitoDomain
      ? {
          oauth: {
            domain: ENV.cognitoDomain,
            redirectSignIn: window.location.origin,
            redirectSignOut: window.location.origin,
            responseType: "code",
            scope: ["email", "openid", "profile"]
          }
        }
      : {
          authenticationFlowType: "USER_SRP_AUTH",
          // Route prod SRP auth + identity-pool creds through the CloudFront named routes;
          // dev/localhost hit Cognito directly. normalizeAmplifyV6Config spreads these into
          // Auth.Cognito. LocalStack uses the same two knobs to redirect both Cognito
          // clients at the container instead of real AWS.
          ...(LOCALSTACK
            ? {
                identityPoolEndpoint: LOCALSTACK.endpoint,
                userPoolEndpoint: LOCALSTACK.endpoint
              }
            : isProdEnv()
              ? {
                  identityPoolEndpoint: PROD_IDENTITY_POOL_ENDPOINT,
                  userPoolEndpoint: PROD_AUTH_ENDPOINT
                }
              : {})
        })
  },
  Storage: {
    AWSS3: {
      bucket: BUCKET_NAME,
      region: REGION
      // No LocalStack endpoint override here on purpose — see LOCALSTACK_S3_ENDPOINT
      // above. It is applied per-call in utils/storage/shared.ts instead.
    },
    customPrefix: {
      private: "",
      protected: "",
      public: ""
    }
  }
});
